/**
 * Die Pforte: Gruppen-Code und Passwort, dann Profilwahl. Wer noch keine
 * Runde hat, gründet hier eine.
 */
import { useState, type FormEvent } from 'react'
import { api, ApiFehler } from '../api/client.js'
import { useSitzung } from '../api/sitzung.js'
import { Amtsknopf, eingabeKlasse, Fehlermeldung, Haarlinie } from '../bausteine/Bausteine.js'

const GRUPPE_KEY = 'strschgb.gruppe'

interface PfortenAntwort {
  token: string
  gruppenName: string
  members: Array<{ id: number; name: string; avatarEmoji: string }>
}

interface ProfilAntwort {
  token: string
  member: { id: number; name: string; avatarEmoji: string }
}

export function Pforte() {
  const { meldeAn } = useSitzung()
  const [gruppe, setGruppe] = useState(localStorage.getItem(GRUPPE_KEY) ?? '')
  const [passwort, setPasswort] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [pforte, setPforte] = useState<PfortenAntwort | null>(null)
  const [gruendet, setGruendet] = useState(false)

  async function anklopfen(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      const antwort = await api.post<PfortenAntwort>('/api/auth/pforte', { gruppe, passwort })
      localStorage.setItem(GRUPPE_KEY, gruppe.trim().toLowerCase())
      setPforte(antwort)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Pforte ist gerade nicht besetzt.')
    } finally {
      setLaeuft(false)
    }
  }

  async function waehleProfil(memberId: number) {
    if (!pforte) return
    setLaeuft(true)
    setFehler(null)
    try {
      const antwort = await fetch('/api/auth/profil', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${pforte.token}` },
        body: JSON.stringify({ memberId }),
      })
      if (!antwort.ok) {
        const nutzlast = (await antwort.json().catch(() => null)) as { meldung?: string } | null
        throw new ApiFehler(antwort.status, 'profil', nutzlast?.meldung ?? 'Profil nicht wählbar.')
      }
      const { token, member } = (await antwort.json()) as ProfilAntwort
      meldeAn(token, member)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Profilwahl ist fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  if (gruendet) return <GruppeGruenden zurueck={() => setGruendet(false)} />

  return (
    <div className="relative z-10 mx-auto flex min-h-dvh max-w-spalte flex-col px-5 pt-10 pb-14">
      <header className="text-center">
        <p className="protokoll">StrSchGb · Der Schnapsrichter</p>
        <h1 className="urteil mt-6">
          Die Pforte
          <br />
          der Kammer
        </h1>
        <p className="mt-4 text-sm text-akte/80">
          {pforte
            ? `Eingelassen bei ${pforte.gruppenName}. Nun das eigene Profil wählen.`
            : 'Gruppen-Code und das gemeinsame Passwort nennen.'}
        </p>
      </header>

      <Haarlinie className="mt-8" />

      {!pforte ? (
        <>
          <form onSubmit={anklopfen} className="mt-8 flex flex-col gap-4">
            <label className="block">
              <span className="protokoll">Gruppen-Code</span>
              <input
                value={gruppe}
                onChange={(e) => setGruppe(e.target.value)}
                className={`mt-1.5 w-full ${eingabeKlasse}`}
                placeholder="squashies"
                autoCapitalize="none"
                autoComplete="username"
              />
            </label>
            <label className="block">
              <span className="protokoll">Gruppen-Passwort</span>
              <input
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                className={`mt-1.5 w-full ${eingabeKlasse}`}
                autoComplete="current-password"
              />
            </label>
            <Amtsknopf type="submit" disabled={laeuft || !gruppe.trim() || !passwort}>
              {laeuft ? 'Wird geprüft …' : 'Anklopfen'}
            </Amtsknopf>
            <Fehlermeldung text={fehler} />
          </form>

          <button
            type="button"
            onClick={() => setGruendet(true)}
            className="protokoll mt-8 text-center normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
          >
            Noch keine Runde? Neue Gruppe gründen
          </button>
        </>
      ) : (
        <div className="mt-8">
          <p className="protokoll">Wer bist du?</p>
          <ul className="mt-3 flex flex-col gap-2">
            {pforte.members.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => void waehleProfil(m.id)}
                  disabled={laeuft}
                  className="flex w-full items-center gap-3 border border-eiche bg-bank px-4 py-3 text-left transition-colors hover:border-messing disabled:opacity-50"
                >
                  <span className="text-2xl" aria-hidden="true">
                    {m.avatarEmoji}
                  </span>
                  <span className="text-lampe">{m.name}</span>
                </button>
              </li>
            ))}
          </ul>
          <Fehlermeldung text={fehler} />
        </div>
      )}
    </div>
  )
}

function GruppeGruenden({ zurueck }: { zurueck: () => void }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [passwort, setPasswort] = useState('')
  const [hostPasswort, setHostPasswort] = useState('')
  const [mitglieder, setMitglieder] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fertig, setFertig] = useState(false)

  async function gruenden(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post('/api/gruppen', {
        name,
        gruppe: code.trim().toLowerCase(),
        passwort,
        hostPasswort,
        mitglieder: mitglieder
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean),
      })
      localStorage.setItem(GRUPPE_KEY, code.trim().toLowerCase())
      setFertig(true)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Gründung ist fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="relative z-10 mx-auto flex min-h-dvh max-w-spalte flex-col px-5 pt-10 pb-14">
      <header className="text-center">
        <p className="protokoll">StrSchGb · Gründungsurkunde</p>
        <h1 className="urteil mt-6">
          Eine Kammer
          <br />
          wird errichtet
        </h1>
      </header>
      <Haarlinie className="mt-8" />

      {fertig ? (
        <div className="mt-8 text-center">
          <p className="text-lampe">Die Gruppe ist gegründet.</p>
          <p className="mt-2 text-sm text-akte/80">
            Gruppen-Code und Passwort an die Runde weitergeben — das Host-Passwort behält, wer die
            Verwaltung führt.
          </p>
          <div className="mt-4">
            <Amtsknopf onClick={zurueck}>Zur Pforte</Amtsknopf>
          </div>
        </div>
      ) : (
        <form onSubmit={gruenden} className="mt-8 flex flex-col gap-4">
          <label className="block">
            <span className="protokoll">Name der Runde</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1.5 w-full ${eingabeKlasse}`}
              placeholder="Die Squashies"
              maxLength={60}
            />
          </label>
          <label className="block">
            <span className="protokoll">Gruppen-Code (für die Anmeldung)</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`mt-1.5 w-full ${eingabeKlasse}`}
              placeholder="nur kleinbuchstaben-und-striche"
              autoCapitalize="none"
            />
          </label>
          <label className="block">
            <span className="protokoll">Gruppen-Passwort (für alle)</span>
            <input
              type="password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              className={`mt-1.5 w-full ${eingabeKlasse}`}
              autoComplete="new-password"
            />
          </label>
          <label className="block">
            <span className="protokoll">Host-Passwort (nur für die Verwaltung)</span>
            <input
              type="password"
              value={hostPasswort}
              onChange={(e) => setHostPasswort(e.target.value)}
              className={`mt-1.5 w-full ${eingabeKlasse}`}
              autoComplete="new-password"
            />
          </label>
          <label className="block">
            <span className="protokoll">Mitglieder, kommagetrennt</span>
            <input
              value={mitglieder}
              onChange={(e) => setMitglieder(e.target.value)}
              className={`mt-1.5 w-full ${eingabeKlasse}`}
              placeholder="Leon, Claudio, Sina"
            />
          </label>
          <Amtsknopf
            type="submit"
            disabled={laeuft || !name.trim() || !code.trim() || passwort.length < 6 || hostPasswort.length < 6 || !mitglieder.trim()}
          >
            {laeuft ? 'Wird beurkundet …' : 'Gruppe gründen'}
          </Amtsknopf>
          <p className="protokoll normal-case">Beide Passwörter brauchen mindestens 6 Zeichen.</p>
          <Fehlermeldung text={fehler} />
          <button
            type="button"
            onClick={zurueck}
            className="protokoll mt-2 text-center normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
          >
            Zurück zur Pforte
          </button>
        </form>
      )}
    </div>
  )
}
